import React, { useState } from "react";
import { T, Modal, Field, Input, Select, Textarea, Btn, Badge, SectionLabel } from "../shared/UI";
import { ALERT_TYPES, PRIORITY, ALERT_STATUS, fmt } from "../../utils/helpers";
import { useApp } from "../../store/AppContext";

// ── Send Alert Modal ───────────────────────────────────────────
export function SendAlertModal({ open, onClose, agents, preAgent, sendAlert, notify }) {
  const [form, setForm] = useState({ agentId: preAgent?.id || "all", type:"message", priority:"normal", title:"", message:"" });
  React.useEffect(() => {
    setForm(f => ({ ...f, agentId: preAgent?.id || "all" }));
  }, [preAgent, open]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const QUICK = [
    { label:"Check In",      message:"Please confirm your current status and location." },
    { label:"Return to Base",message:"Return to your assigned base immediately." },
    { label:"Stay Position", message:"Hold your current position and await further instructions." },
    { label:"Zone Change",   message:"Proceed to the newly assigned zone as instructed." },
  ];

  const handleSend = async () => {
    if (!form.title || !form.message) { notify("error","Title and message required"); return; }
    await sendAlert({ agentId: form.agentId, type: form.type, title: form.title, message: form.message, priority: form.priority });
    notify("success", form.agentId === "all" ? "Broadcast sent to all agents" : "Alert sent to agent");
    onClose();
    setForm({ agentId:"all", type:"message", priority:"normal", title:"", message:"" });
  };

  const at = ALERT_TYPES[form.type];

  return (
    <Modal open={open} onClose={onClose} title="Send Alert to Agent" width={520}>
      {/* SOS shortcut */}
      <div style={styles.sosBanner} onClick={() => { setForm(f => ({ ...f, type:"sos", priority:"critical", title:"🚨 SOS — URGENT", message:"SOS issued by admin — respond immediately to field request." })); }}>
        🚨 Click to set as EMERGENCY SOS
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <Field label="RECIPIENT">
          <Select value={form.agentId} onChange={set("agentId")}>
            <option value="all">📢 All Agents (Broadcast)</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name} — {a.zone}</option>)}
          </Select>
        </Field>
        <Field label="TYPE">
          <Select value={form.type} onChange={set("type")}>
            {Object.entries(ALERT_TYPES).map(([k,v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </Select>
        </Field>
        <div style={{ gridColumn:"1/-1" }}>
          <Field label="PRIORITY">
            <div style={{ display:"flex", gap:8 }}>
              {Object.entries(PRIORITY).map(([k,v]) => (
                <div key={k} style={{ ...styles.priorityOpt, ...(form.priority===k?{ borderColor:v.color, background:v.bg }:{}) }} onClick={() => setForm(f=>({...f, priority:k}))}>
                  <div style={{ width:7,height:7,borderRadius:"50%",background:v.color }} />{v.label}
                </div>
              ))}
            </div>
          </Field>
        </div>
        <div style={{ gridColumn:"1/-1" }}>
          <Field label="TITLE"><Input value={form.title} onChange={set("title")} placeholder="Alert title..." /></Field>
        </div>
        <div style={{ gridColumn:"1/-1" }}>
          <Field label="MESSAGE">
            <Textarea value={form.message} onChange={set("message")} placeholder="Write your message to the agent..." rows={4} />
          </Field>
        </div>
      </div>

      {/* Quick templates */}
      <div style={{ marginTop:4 }}>
        <SectionLabel>QUICK TEMPLATES</SectionLabel>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {QUICK.map(q => (
            <div key={q.label} style={styles.template} onClick={() => setForm(f=>({...f, title:q.label, message:q.message}))}>
              {q.label}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display:"flex", gap:10, marginTop:16 }}>
        <Btn variant="ghost" full onClick={onClose}>Cancel</Btn>
        <Btn variant={form.type==="sos"?"danger":"primary"} full onClick={handleSend}>
          {form.type==="sos"?"🚨 Send SOS":"Send Alert"}
        </Btn>
      </div>
    </Modal>
  );
}

// ── Alerts List View ──────────────────────────────────────────
export default function AlertsView({ alerts, agents, sendAlert, cancelAlert, notify }) {
  const { openAlertModal } = useApp();
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const filtered = alerts.filter(a => {
    const ms = filter === "all" || a.status === filter;
    const mt = typeFilter === "all" || a.type === typeFilter;
    return ms && mt;
  });

  return (
    <div style={styles.wrap}>
      {/* Top toolbar */}
      <div style={styles.toolbar}>
        <div style={{ flex:1 }}>
          <div style={styles.filterRow}>
            {["all","active","sent","ack"].map(s => (
              <button key={s} style={{ ...styles.fBtn, ...(filter===s?styles.fBtnA:{}) }} onClick={() => setFilter(s)}>
                {ALERT_STATUS[s]?.label || s.toUpperCase()}
                {s !== "all" && <span style={{ ...styles.fCount, background: s==="active"?T.sos:s==="sent"?T.acc4:T.acc3 }}>
                  {alerts.filter(a=>a.status===s).length}
                </span>}
              </button>
            ))}
          </div>
          <div style={{ ...styles.filterRow, marginTop:6 }}>
            {["all",...Object.keys(ALERT_TYPES)].map(t => (
              <button key={t} style={{ ...styles.fBtn, ...(typeFilter===t?styles.fBtnA:{}) }} onClick={() => setTypeFilter(t)}>
                {t !== "all" && ALERT_TYPES[t]?.icon} {(ALERT_TYPES[t]?.label||t).toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <Btn variant="primary" icon="📤" onClick={() => openAlertModal(null)}>New Alert</Btn>
      </div>

      {/* Stats row */}
      <div style={styles.statsRow}>
        {[
          { label:"Total Sent",   val: alerts.length,                                   color:T.acc  },
          { label:"Active / SOS", val: alerts.filter(a=>a.status==="active").length,    color:T.sos  },
          { label:"Pending Ack",  val: alerts.filter(a=>a.status==="sent").length,      color:T.acc4 },
          { label:"Acknowledged", val: alerts.filter(a=>a.status==="ack").length,       color:T.acc3 },
        ].map(s => (
          <div key={s.label} style={styles.statCard}>
            <div style={{ fontFamily:T.fontMono, fontSize:26, fontWeight:700, color:s.color }}>{s.val}</div>
            <div style={{ fontSize:11, color:T.muted, marginTop:3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Alert list */}
      <div style={styles.list}>
        {filtered.map(alert => <AlertRow key={alert.id} alert={alert} onCancel={() => cancelAlert(alert.id)} />)}
        {filtered.length === 0 && <div style={{ color:T.muted, textAlign:"center", padding:"40px" }}>No alerts match your filter</div>}
      </div>
    </div>
  );
}

function AlertRow({ alert, onCancel }) {
  const at = ALERT_TYPES[alert.type] || ALERT_TYPES.message;
  const pr = PRIORITY[alert.priority] || PRIORITY.normal;
  const st = ALERT_STATUS[alert.status] || ALERT_STATUS.sent;

  return (
    <div style={{ ...styles.alertRow, ...(alert.status==="active"?{ borderColor:"rgba(255,68,102,0.4)", background:"rgba(255,68,102,0.04)" }:{}) }}>
      <div style={{ ...styles.alertIcon, background:`${at.color}15`, color:at.color }}>{at.icon}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
          <span style={{ fontSize:14, fontWeight:700, color:T.text }}>{alert.title}</span>
          <Badge color={pr.color} bg={pr.bg} border="transparent">{pr.label}</Badge>
          <Badge color={st.color} bg={`${st.color}15`} border="transparent">● {st.label}</Badge>
        </div>
        <div style={{ fontSize:12, color:T.muted, marginBottom:6, lineHeight:1.5 }}>{alert.message}</div>
        <div style={{ display:"flex", gap:16, fontSize:11, fontFamily:T.fontMono, color:T.muted2 }}>
          <span>👤 {alert.agentName}</span>
          <span>📤 {fmt.datetime(alert.sentAt)}</span>
          {alert.ackAt && <span style={{ color:T.acc3 }}>✓ ACK {fmt.datetime(alert.ackAt)}</span>}
        </div>
      </div>
      {alert.status !== "ack" && (
        <button style={styles.cancelBtn} onClick={onCancel} title="Cancel alert">×</button>
      )}
    </div>
  );
}

const styles = {
  wrap: { flex:1, display:"flex", flexDirection:"column", overflow:"hidden" },
  toolbar: { display:"flex", alignItems:"flex-start", gap:12, padding:"14px 20px", borderBottom:`1px solid ${T.border}`, flexShrink:0 },
  filterRow: { display:"flex", gap:4, flexWrap:"wrap" },
  fBtn: { padding:"4px 10px", borderRadius:6, border:`1px solid ${T.border}`, background:"none", color:T.muted, fontFamily:T.fontMono, fontSize:9, cursor:"pointer", letterSpacing:"0.5px", display:"flex", alignItems:"center", gap:4 },
  fBtnA: { borderColor:T.acc, color:T.acc, background:"rgba(124,106,247,0.08)" },
  fCount: { borderRadius:20, padding:"1px 5px", fontSize:9, color:"#fff", fontWeight:700 },
  statsRow: { display:"flex", gap:12, padding:"14px 20px", borderBottom:`1px solid ${T.border}`, flexShrink:0 },
  statCard: { background:T.s2, border:`1px solid ${T.border}`, borderRadius:10, padding:"12px 18px", flex:1 },
  list: { flex:1, overflowY:"auto", padding:"14px 20px", display:"flex", flexDirection:"column", gap:10 },
  alertRow: { background:T.s2, border:`1px solid ${T.border}`, borderRadius:10, padding:"14px", display:"flex", gap:12, alignItems:"flex-start", transition:"border-color 0.2s" },
  alertIcon: { width:38, height:38, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 },
  cancelBtn: { background:"none", border:`1px solid ${T.border}`, color:T.muted, borderRadius:6, padding:"4px 8px", cursor:"pointer", fontSize:16, flexShrink:0 },
  sosBanner: { background:"rgba(255,68,102,0.1)", border:"1px solid rgba(255,68,102,0.35)", borderRadius:8, padding:"8px 12px", fontSize:12, fontFamily:T.fontMono, color:T.sos, cursor:"pointer", marginBottom:16, textAlign:"center", transition:"all 0.2s" },
  priorityOpt: { display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:6, border:`1px solid ${T.border}`, cursor:"pointer", fontSize:11, fontFamily:T.fontMono, color:T.muted, flex:1, justifyContent:"center", transition:"all 0.18s" },
  template: { padding:"5px 12px", borderRadius:6, border:`1px solid ${T.border2}`, fontSize:11, color:T.muted, cursor:"pointer", fontFamily:T.fontMono, transition:"all 0.18s" },
};
